(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const createToast = () => {
    let toast = document.querySelector('[data-ccv1-toast]');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'ccv1-toast';
    toast.dataset.ccv1Toast = '';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
    return toast;
  };

  let toastTimer;
  const announce = (message, isError = false) => {
    const toast = createToast();
    toast.textContent = message;
    toast.style.background = isError ? 'var(--ccv1-pink-soft)' : 'var(--ccv1-mango)';
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3600);
  };

  const cartAddUrl = () => {
    const route = window.routes?.cart_add_url || '/cart/add';
    return route.endsWith('.js') ? route : `${route}.js`;
  };

  const formatMoney = (cents) => {
    const amount = Number(cents) / 100;
    const wholeAmount = Number.isInteger(amount);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        minimumFractionDigits: wholeAmount ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(wholeAmount ? 0 : 2)}`;
    }
  };

  const syncCartUi = (cart) => {
    if (!cart || typeof cart.item_count !== 'number') return;

    document.querySelectorAll('[data-ccv1-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
    });
    document.querySelectorAll('.ccv1-header__cart').forEach((link) => {
      link.setAttribute('aria-label', `Cart with ${cart.item_count} items`);
    });

    const cartTop = document.querySelector('[data-ccv1-cart-top]');
    const shipping = cartTop?.querySelector('[data-ccv1-cart-shipping]');
    if (!cartTop || !shipping || typeof cart.total_price !== 'number') return;

    shipping.hidden = cart.item_count === 0;
    if (cart.item_count === 0) return;

    const threshold = Number(cartTop.dataset.shippingThreshold);
    if (!Number.isFinite(threshold) || threshold <= 0) return;
    const remaining = Math.max(0, threshold - cart.total_price);
    const progress = Math.min(100, Math.max(0, (cart.total_price / threshold) * 100));
    const heading = shipping.querySelector('[data-ccv1-shipping-heading]');
    const copy = shipping.querySelector('[data-ccv1-shipping-copy]');
    const track = shipping.querySelector('[data-ccv1-shipping-track]');
    const fill = track?.querySelector('span');

    if (remaining > 0) {
      if (heading) heading.textContent = `${formatMoney(remaining)} away from free U.S. shipping`;
      if (copy) copy.textContent = 'Add another pack or continue to checkout.';
    } else {
      if (heading) heading.textContent = 'Free U.S. shipping unlocked';
      if (copy) copy.textContent = 'Free shipping applies at checkout.';
    }
    if (track) track.setAttribute('aria-label', `${Math.round(progress)} percent toward free shipping`);
    if (fill) fill.style.width = `${progress}%`;
  };

  const parseBundle = (value) => {
    if (!value) return [];
    return value
      .split(',')
      .map((entry) => {
        const [id, quantity = '1'] = entry.split(':');
        return { id: Number(id), quantity: Number(quantity) };
      })
      .filter((item) => Number.isInteger(item.id) && item.id > 0 && Number.isInteger(item.quantity) && item.quantity > 0);
  };

  let cartRefreshSequence = 0;
  let lastKnownCart = null;
  let reconcileProgressiveCartPricing = null;
  const publishCartState = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return;
    window.dispatchEvent(new CustomEvent('ccv1:cart-state', { detail: { cart } }));
  };
  const refreshCartCount = async ({ duringMutation = false } = {}) => {
    if (cartMutationBusy && !duringMutation) return lastKnownCart;
    const requestSequence = ++cartRefreshSequence;
    try {
      const response = await fetch('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      let cart = await response.json();
      if (requestSequence !== cartRefreshSequence) return null;
      if (typeof reconcileProgressiveCartPricing === 'function') {
        cart = await reconcileProgressiveCartPricing(cart);
      }
      if (requestSequence !== cartRefreshSequence) return null;
      lastKnownCart = cart;
      syncCartUi(cart);
      renderPdpCartState(cart);
      publishCartState(cart);
      return cart;
    } catch (_error) {
      // The add succeeded; a count refresh failure should not overwrite that state.
      return null;
    }
  };

  const excludedRewardFamilies = new Set(['variety', 'mega', 'stockup']);
  const progressiveEligiblePairs = new Map([
    [54091373216049, '8OZ-2PK-MANGO-BITES'],
    [54091393007921, '8OZ-2PK-PEACH'],
    [54091386421553, '8OZ-2PK-STRAWBERRYBITE'],
    [54091379671345, '8OZ-2PK-WATERMELON'],
  ]);
  const progressiveBasePairPrice = 1599;
  const fallbackProgressiveLadder = [1599, 2999, 4299, 5436, 6795, 7999];
  const fallbackProgressiveDiscountCodes = [
    'CCV1-4BAGS-2999',
    'CCV1-6BAGS-4299',
    'CCV1-8BAGS-5436',
    'CCV1-10BAGS-6795',
    'CCV1-12BAGS-7999',
  ];
  const legacyProgressiveDiscountCodes = ['CCV1-8BAGS-5499', 'CCV1-10BAGS-6799'];
  const progressiveDiscountCodeSet = new Set([
    ...fallbackProgressiveDiscountCodes,
    ...legacyProgressiveDiscountCodes,
  ]);

  const getProgressiveConfigRoot = () => document.querySelector('[data-ccv1-progressive-config]')
    || document.querySelector('[data-ccv1-flavor-pdp][data-ccv1-progressive-pricing-verified]');

  const getItemOfferFamily = (item) => {
    const properties = item?.properties || {};
    return String(properties._ccv1_offer_family || properties.ccv1_offer_family || '').toLowerCase();
  };

  const hasFixedBundle = (cart) => (cart?.items || []).some((item) => excludedRewardFamilies.has(getItemOfferFamily(item)));
  const isUsdCart = (cart) => (cart?.currency || window.Shopify?.currency?.active || 'USD') === 'USD';

  const getItemPackSize = (item) => {
    const propertyBagCount = Number(item?.properties?._ccv1_bag_count);
    if (Number.isFinite(propertyBagCount) && propertyBagCount > 0) return propertyBagCount;

    const titleMatch = String(item?.variant_title || item?.title || '').match(/(\d+)\s*pack/i);
    if (titleMatch) return Number(titleMatch[1]);

    const skuMatch = String(item?.sku || '').match(/(?:^|-)\s*(\d+)PK(?:-|$)/i);
    return skuMatch ? Number(skuMatch[1]) : 1;
  };

  const isProgressiveEligibleItem = (item) => {
    const family = getItemOfferFamily(item);
    if (excludedRewardFamilies.has(family)) return false;
    const variantId = Number(item?.variant_id || item?.id || 0);
    const expectedSku = progressiveEligiblePairs.get(variantId);
    return getItemPackSize(item) === 2 && expectedSku === String(item?.sku || '').trim();
  };

  const getItemDiscounts = (item) => {
    if (Array.isArray(item?.discounts) && item.discounts.length) {
      return item.discounts.map((discount) => ({
        title: String(discount.title || ''),
        amount: Number(discount.amount || 0),
      }));
    }

    return (Array.isArray(item?.line_level_discount_allocations)
      ? item.line_level_discount_allocations
      : []).map((allocation) => ({
      title: String(allocation.discount_application?.title || allocation.title || ''),
      amount: Number(allocation.amount || 0),
    }));
  };

  const summarizeCart = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let bagCount = 0;
    let eligiblePairQuantity = 0;
    let eligibleOriginalSubtotal = 0;

    items.forEach((item) => {
      const quantity = Number(item.quantity || 0);
      const packSize = getItemPackSize(item);
      bagCount += packSize * quantity;
      if (isProgressiveEligibleItem(item)) {
        eligiblePairQuantity += quantity;
        eligibleOriginalSubtotal += Number(item.original_line_price ?? item.final_line_price ?? 0);
      }
    });

    return {
      bagCount,
      pairQuantity: eligiblePairQuantity,
      eligiblePairQuantity,
      eligibleOriginalSubtotal,
      originalTotal: Number(cart?.original_total_price || 0),
      totalDiscount: Number(cart?.total_discount || 0),
      finalTotal: Number(cart?.total_price || 0),
      itemCount: Number(cart?.item_count || 0),
    };
  };

  const parseProgressiveLadder = (value) => {
    const parsed = String(value || '')
      .split(',')
      .map((entry) => Math.round(Number(entry.trim()) * 100))
      .filter((amount) => Number.isInteger(amount) && amount > 0);
    if (parsed.length !== fallbackProgressiveLadder.length) return fallbackProgressiveLadder;
    if (parsed.some((amount, index) => amount !== fallbackProgressiveLadder[index])) return fallbackProgressiveLadder;
    return parsed;
  };

  const parseProgressiveDiscountCodes = (value) => {
    const parsed = String(value || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (parsed.length !== fallbackProgressiveDiscountCodes.length) return fallbackProgressiveDiscountCodes;
    if (parsed.some((code, index) => code !== fallbackProgressiveDiscountCodes[index])) {
      return fallbackProgressiveDiscountCodes;
    }
    return parsed;
  };

  const getProgressiveDiscountCode = (root, pairQuantity) => {
    if (!root || pairQuantity < 2) return '';
    const codes = parseProgressiveDiscountCodes(root.dataset.ccv1ProgressiveDiscountCodes);
    return codes[Math.min(pairQuantity - 2, codes.length - 1)] || '';
  };

  const getTierTotal = (pairQuantity, ladder) => {
    if (pairQuantity <= 0) return 0;
    if (pairQuantity <= ladder.length) return ladder[pairQuantity - 1];
    return ladder[ladder.length - 1] + ((pairQuantity - ladder.length) * progressiveBasePairPrice);
  };

  const getCartDiscountCodes = (cart) => (Array.isArray(cart?.discount_codes) ? cart.discount_codes : [])
    .filter((discount) => discount?.code)
    .map((discount) => String(discount.code).trim())
    .filter(Boolean);

  const getAppliedDiscountCodes = (cart) => (Array.isArray(cart?.discount_codes) ? cart.discount_codes : [])
    .filter((discount) => discount && discount.applicable !== false)
    .map((discount) => String(discount.code || '').trim())
    .filter(Boolean);

  const getProgressiveDiscountEvidence = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let eligibleDiscount = 0;
    let excludedDiscount = 0;

    items.forEach((item) => {
      const discounts = getItemDiscounts(item);
      const allocated = discounts.reduce((total, discount) => total + discount.amount, 0);
      if (isProgressiveEligibleItem(item)) {
        eligibleDiscount += allocated;
      } else {
        excludedDiscount += allocated;
      }
    });

    const cartApplications = Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : [];
    cartApplications.forEach((application) => {
      const allocated = Number(application.total_allocated_amount || 0);
      if (allocated <= 0) return;
      const hasNonEligibleItems = items.some((item) => !isProgressiveEligibleItem(item));
      if (hasNonEligibleItems) excludedDiscount += allocated;
      else eligibleDiscount += allocated;
    });

    return { eligibleDiscount, excludedDiscount };
  };

  const renderPdpCartState = (cart) => {
    if (!cart) return;
    const summary = summarizeCart(cart);

    document.querySelectorAll('[data-ccv1-flavor-pdp]').forEach((root) => {
      const ladder = parseProgressiveLadder(root.dataset.ccv1ProgressiveLadder);
      const pricingConfigured = root.dataset.ccv1ProgressivePricingVerified === 'true';
      const isVarietyMode = root.dataset.ccv1PackMode === 'variety';
      const selectedPairPrice = Number(root.dataset.ccv1SelectedPriceCents) || 0;
      const selectedProgressiveEligible = root.dataset.ccv1SelectedProgressiveEligible === 'true'
        && selectedPairPrice === progressiveBasePairPrice;
      const isPickleOffer = root.dataset.ccv1SelectedHandle === 'pickle-belts';
      const selectedShowsRewardMeter = selectedProgressiveEligible || isPickleOffer;
      const pairQuantity = summary.eligiblePairQuantity;
      const currentTierTotal = getTierTotal(pairQuantity, ladder);
      const expectedCurrentSaving = Math.max(0, (pairQuantity * progressiveBasePairPrice) - currentTierTotal);
      const expectedDiscountCode = getProgressiveDiscountCode(root, pairQuantity);
      const hasCustomerDiscountCode = getCartDiscountCodes(cart)
        .some((code) => !progressiveDiscountCodeSet.has(code));
      const appliedDiscountCodes = getAppliedDiscountCodes(cart);
      const appliedCcv1Codes = appliedDiscountCodes.filter((code) => progressiveDiscountCodeSet.has(code));
      const discountEvidence = getProgressiveDiscountEvidence(cart);
      const basePricingMatches = summary.eligibleOriginalSubtotal === pairQuantity * progressiveBasePairPrice;
      const eligibleNetMatches = summary.eligibleOriginalSubtotal - discountEvidence.eligibleDiscount === currentTierTotal;
      const discountCodeMatches = pairQuantity === 1
        ? appliedCcv1Codes.length === 0
        : appliedCcv1Codes.length === 1 && appliedCcv1Codes[0] === expectedDiscountCode;
      const currentPricingVerified = pricingConfigured
        && isUsdCart(cart)
        && !hasFixedBundle(cart)
        && pairQuantity >= 1
        && basePricingMatches
        && eligibleNetMatches
        && discountEvidence.eligibleDiscount === expectedCurrentSaving
        && discountEvidence.excludedDiscount === 0
        && summary.totalDiscount === expectedCurrentSaving
        && !hasCustomerDiscountCode
        && discountCodeMatches;
      const hasApprovedNextTier = pairQuantity >= 1 && pairQuantity < ladder.length;
      const nextPairQuantity = pairQuantity + 1;
      const canAdvertiseNextPair = currentPricingVerified && hasApprovedNextTier && selectedProgressiveEligible;
      const actualSaving = Math.max(0, summary.totalDiscount);

      root.querySelectorAll('[data-ccv1-inline-cart]').forEach((state) => {
        state.hidden = summary.itemCount === 0;
        if (summary.itemCount === 0) return;

        const size = state.querySelector('[data-ccv1-cart-size]');
        const total = state.querySelector('[data-ccv1-cart-total]');
        const summarySaving = state.querySelector('[data-ccv1-cart-summary-saving]');
        const summarySavingValue = state.querySelector('[data-ccv1-cart-summary-saving-value]');
        if (size) size.textContent = `${summary.bagCount} ${summary.bagCount === 1 ? 'bag' : 'bags'}`;
        if (total) total.textContent = formatMoney(summary.finalTotal);
        if (summarySaving) summarySaving.hidden = actualSaving <= 0;
        if (summarySavingValue) summarySavingValue.textContent = formatMoney(actualSaving);
      });

      const shippingThreshold = Number(root.dataset.ccv1ShippingThresholdCents) || 0;
      root.querySelectorAll('[data-ccv1-cart-progress]').forEach((progress) => {
        const shipping = progress.querySelector('[data-ccv1-cart-shipping]');
        const shippingCopy = progress.querySelector('[data-ccv1-cart-shipping-copy]');
        const shippingLabel = progress.querySelector('[data-ccv1-cart-shipping-label]');
        const shippingTrack = progress.querySelector('[data-ccv1-cart-shipping-track]');
        const milestones = progress.querySelector('[data-ccv1-discount-milestones]');

        const hasShippingThreshold = shippingThreshold > 0;
        if (shipping) shipping.hidden = !hasShippingThreshold;
        if (shippingTrack) shippingTrack.hidden = !hasShippingThreshold;
        const shippingRemaining = Math.max(0, shippingThreshold - summary.finalTotal);
        const shippingUnlocked = hasShippingThreshold && shippingRemaining === 0;
        const shippingProgress = hasShippingThreshold
          ? Math.min(1, Math.max(0, summary.finalTotal / shippingThreshold))
          : 0;
        // Pickle keeps the shared cart-goal meter, but remains excluded from the
        // 8 oz progressive-discount payload and verification rules above.
        const canPreviewDiscountSteps = pairQuantity === 0
          && isUsdCart(cart)
          && !hasFixedBundle(cart)
          && !isVarietyMode
          && selectedShowsRewardMeter
          && pricingConfigured;
        const hasVerifiedDiscountSteps = !isVarietyMode
          && currentPricingVerified
          && pairQuantity >= 1;
        const showDiscountSteps = canPreviewDiscountSteps || hasVerifiedDiscountSteps;
        const showShippingOnly = summary.itemCount > 0 && hasShippingThreshold;

        progress.hidden = !showDiscountSteps && !showShippingOnly;
        progress.dataset.ccv1ProgressMode = showDiscountSteps ? 'steps' : 'shipping';
        progress.dataset.ccv1ShippingUnlocked = String(shippingUnlocked);
        if (milestones) milestones.hidden = !showDiscountSteps;

        let displayProgress = shippingProgress;
        let progressText = shippingUnlocked
          ? 'Free shipping unlocked'
          : `${formatMoney(shippingRemaining)} until free shipping`;

        if (showDiscountSteps) {
          const atStart = pairQuantity === 0;
          const fiveUnlocked = pairQuantity >= 2;
          const tenUnlocked = pairQuantity >= 3;
          const fifteenUnlocked = pairQuantity >= 4;
          const shippingRewardUnlocked = pairQuantity >= 5 && shippingUnlocked;
          const milestoneStates = {
            start: atStart ? 'current' : 'unlocked',
            five: fiveUnlocked ? 'unlocked' : 'next',
            ten: tenUnlocked ? 'unlocked' : (fiveUnlocked ? 'next' : 'locked'),
            fifteen: fifteenUnlocked ? 'unlocked' : (tenUnlocked ? 'next' : 'locked'),
            shipping: shippingRewardUnlocked ? 'unlocked' : (fifteenUnlocked ? 'next' : 'locked'),
          };
          const milestonesHydrated = progress.dataset.ccv1MilestonesHydrated === 'true';
          Object.entries(milestoneStates).forEach(([name, state]) => {
            const milestone = milestones?.querySelector(`[data-ccv1-milestone="${name}"]`);
            if (!milestone) return;

            const justUnlocked = name !== 'start'
              && milestonesHydrated
              && state === 'unlocked'
              && milestone.dataset.state !== 'unlocked';

            milestone.dataset.state = state;

            if (justUnlocked) {
              milestone.classList.remove('is-celebrating');
              void milestone.offsetWidth;
              milestone.classList.add('is-celebrating');
              window.setTimeout(() => milestone.classList.remove('is-celebrating'), 900);
            }
          });
          progress.dataset.ccv1MilestonesHydrated = 'true';

          const startMilestone = milestones?.querySelector('[data-ccv1-milestone="start"]');
          const startMilestoneLabel = startMilestone?.querySelector('strong');
          const startMilestoneCount = startMilestone?.querySelector('small');
          if (startMilestoneLabel) startMilestoneLabel.textContent = isPickleOffer ? 'BITES' : 'START';
          if (startMilestoneCount) startMilestoneCount.textContent = `${pairQuantity * 2} BAGS`;

          if (atStart) {
            if (shippingLabel) shippingLabel.textContent = isPickleOffer ? 'Bites offer' : 'Start here';
            if (shippingCopy) shippingCopy.textContent = isPickleOffer ? 'Bites: 5%+ off at 4 bags' : '5%+ off is next';
            displayProgress = 0;
            progressText = isPickleOffer
              ? 'No eligible Bites bags yet. Add two Bites bags to unlock five percent or more off'
              : 'Start here. Five percent or more off is the next goal';
          } else if (!fiveUnlocked) {
            if (shippingLabel) shippingLabel.textContent = 'Next goal';
            if (shippingCopy) shippingCopy.textContent = '5%+ off';
            displayProgress = 1 / 8;
            progressText = 'Next goal is five percent or more off';
          } else if (!tenUnlocked) {
            if (shippingLabel) shippingLabel.textContent = 'Next goal';
            if (shippingCopy) shippingCopy.textContent = '10%+ off';
            displayProgress = 1 / 4;
            progressText = 'Five percent or more off unlocked. Next add unlocks ten percent or more off';
          } else if (!fifteenUnlocked) {
            if (shippingLabel) shippingLabel.textContent = 'Next goal';
            if (shippingCopy) shippingCopy.textContent = '15%+ off';
            displayProgress = 1 / 2;
            progressText = 'Ten percent or more off unlocked. Next add unlocks fifteen percent or more off';
          } else if (!shippingRewardUnlocked) {
            if (shippingLabel) shippingLabel.textContent = 'Next goal';
            if (shippingCopy) shippingCopy.textContent = 'Free shipping';
            displayProgress = 3 / 4;
            progressText = 'Fifteen percent or more off unlocked. Free shipping is next';
          } else {
            if (shippingLabel) shippingLabel.textContent = 'Free shipping';
            if (shippingCopy) shippingCopy.textContent = 'Unlocked';
            displayProgress = 1;
            progressText = 'Free shipping unlocked';
          }
        } else {
          if (shippingLabel) shippingLabel.textContent = shippingUnlocked ? 'Free shipping' : 'Free shipping';
          if (shippingCopy) shippingCopy.textContent = shippingUnlocked ? 'Unlocked' : `Add ${formatMoney(shippingRemaining)}`;
        }

        progress.style.setProperty('--ccv1-shipping-progress', displayProgress.toFixed(4));
        if (shippingTrack) {
          shippingTrack.setAttribute('aria-valuenow', String(Math.round(displayProgress * 100)));
          shippingTrack.setAttribute('aria-valuetext', progressText);
        }
      });

      root.querySelectorAll('[data-ccv1-individual-add]').forEach((button) => {
        if (button.dataset.ccv1OfferFamily !== 'individual') return;
        const label = button.querySelector('[data-ccv1-button-label]');
        const saving = button.querySelector('[data-ccv1-button-saving]');
        if (!label) return;
        const buttonEligible = button.dataset.ccv1ProgressiveEligible === 'true';
        const available = button.dataset.ccv1Available !== 'false' && button.dataset.available !== 'false';
        const selectedTitle = root.dataset.ccv1SelectedProduct || root.dataset.ccv1ProductTitle || 'Chili Chews';
        let idleLabel;
        if (!available) idleLabel = 'Sold out';
        else if (canAdvertiseNextPair && buttonEligible) {
          idleLabel = 'Add to cart';
          if (saving) {
            saving.hidden = true;
            saving.textContent = '';
          }
          button.dataset.ccv1NextPairOffer = 'true';
          button.dataset.ccv1TierTargetQuantity = String(nextPairQuantity);
          button.dataset.ccv1DiscountCode = getProgressiveDiscountCode(root, nextPairQuantity);
          button.setAttribute('aria-label', `Add ${selectedTitle} to cart`);
        } else {
          idleLabel = 'Add to cart';
          button.dataset.ccv1NextPairOffer = 'false';
          button.dataset.ccv1TierTargetQuantity = '';
          button.dataset.ccv1DiscountCode = '';
          button.setAttribute('aria-label', `Add ${selectedTitle} to cart`);
          if (saving) {
            saving.hidden = true;
            saving.textContent = '';
          }
        }
        button.dataset.ccv1IdleLabel = idleLabel;
        if (button.getAttribute('aria-busy') !== 'true') label.textContent = idleLabel;
      });

      const mobileBuy = root.querySelector('[data-ccv1-mobile-buy]');
      const mobileForm = mobileBuy?.querySelector('form');
      const mobileAdd = mobileBuy?.querySelector('[data-ccv1-mobile-add]');
      const mobileCheckout = mobileBuy?.querySelector('[data-ccv1-mobile-checkout]');
      const mobileTitle = mobileBuy?.querySelector('[data-ccv1-mobile-title]');
      const mobileVariant = mobileBuy?.querySelector('[data-ccv1-mobile-variant]');
      const hasCart = summary.itemCount > 0;
      if (mobileBuy) mobileBuy.dataset.ccv1HasCart = String(hasCart);
      if (mobileForm) mobileForm.hidden = false;
      if (mobileAdd) mobileAdd.hidden = false;
      if (mobileCheckout) {
        mobileCheckout.hidden = !hasCart;
        const label = mobileCheckout.querySelector('[data-ccv1-button-label]');
        const meta = mobileCheckout.querySelector('[data-ccv1-mobile-checkout-meta]');
        if (label) label.textContent = 'Go to checkout';
        if (meta) meta.textContent = hasCart
          ? `${summary.bagCount} ${summary.bagCount === 1 ? 'bag' : 'bags'} · ${formatMoney(summary.finalTotal)}`
          : '';
      }
      if (hasCart) {
        if (mobileTitle) mobileTitle.textContent = 'Your cart';
        if (mobileVariant) mobileVariant.textContent = `${summary.bagCount} ${summary.bagCount === 1 ? 'bag' : 'bags'} · ${formatMoney(summary.finalTotal)}`;
      } else if (!isVarietyMode) {
        if (mobileTitle) mobileTitle.textContent = root.dataset.ccv1SelectedProduct || root.dataset.ccv1ProductTitle || 'Chili Chews';
        if (mobileVariant) {
          const selectedPrice = Number(root.dataset.ccv1SelectedPriceCents);
          const selectedBagCount = Math.max(1, Number(root.dataset.ccv1SelectedBagCount) || 2);
          const selectedBagLabel = `${selectedBagCount} ${selectedBagCount === 1 ? 'bag' : 'bags'}`;
          mobileVariant.textContent = Number.isFinite(selectedPrice) ? `${selectedBagLabel} · ${formatMoney(selectedPrice)}` : selectedBagLabel;
        }
      }
    });

    window.dispatchEvent(new Event('ccv1:sticky-refresh'));
  };

  const openCartNotification = (result, cart, trigger) => {
    const notification = document.querySelector('cart-notification');
    const productContainer = document.getElementById('cart-notification-product');
    if (!notification || !productContainer || typeof notification.open !== 'function') return false;

    const addedItems = Array.isArray(result?.items) ? result.items : [];
    const firstItem = addedItems[0];
    const notificationPanel = notification.querySelector('#cart-notification');
    const wasActive = notificationPanel?.classList.contains('active') === true;
    productContainer.replaceChildren();

    if (firstItem) {
      const imageUrl = firstItem.featured_image?.url || firstItem.image || '';
      if (imageUrl) {
        const imageWrap = document.createElement('div');
        imageWrap.className = 'cart-notification-product__image global-media-settings';
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = firstItem.featured_image?.alt || firstItem.product_title || '';
        image.width = 70;
        image.height = 70;
        image.loading = 'eager';
        imageWrap.appendChild(image);
        productContainer.appendChild(imageWrap);
      }

      const details = document.createElement('div');
      const name = document.createElement('h3');
      name.className = 'cart-notification-product__name h4';
      name.textContent = addedItems.length > 1 ? `${addedItems.length} packs added` : (firstItem.product_title || firstItem.title || 'Added to cart');
      details.appendChild(name);
      if (addedItems.length === 1 && firstItem.variant_title && firstItem.variant_title !== 'Default Title') {
        const variant = document.createElement('p');
        variant.className = 'ccv1-cart-notification__variant';
        variant.textContent = firstItem.variant_title;
        details.appendChild(variant);
      }
      productContainer.appendChild(details);
    }

    const viewCart = document.getElementById('cart-notification-button');
    if (viewCart && cart) viewCart.textContent = `View cart (${cart.item_count})`;

    if (wasActive) viewCart?.focus();
    if (wasActive) return true;
    if (typeof notification.setActiveElement === 'function' && trigger && !trigger.disabled) notification.setActiveElement(trigger);
    notification.open();
    return true;
  };

  const updateCart = async ({ discount, updates } = {}) => {
    const body = {};
    if (typeof discount === 'string') body.discount = discount;
    if (updates && Object.keys(updates).length) body.updates = updates;
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || cart.message || 'Cart update failed');
    lastKnownCart = cart;
    syncCartUi(cart);
    renderPdpCartState(cart);
    const liveTotal = document.getElementById('cart-live-region-text');
    if (liveTotal) liveTotal.textContent = `New estimated total: ${formatMoney(cart.total_price)}`;
    publishCartState(cart);
    return cart;
  };

  let progressiveReconcilePromise = null;
  const runPricingReconciliation = async (task) => {
    const ownsLock = !cartMutationBusy;
    if (ownsLock) beginCartMutation(false);
    progressiveReconcilePromise = task();
    try { return await progressiveReconcilePromise; } finally {
      progressiveReconcilePromise = null;
      if (ownsLock) endCartMutation();
    }
  };
  reconcileProgressiveCartPricing = async (cart) => {
    if (!cart) return cart;
    if (progressiveReconcilePromise) return progressiveReconcilePromise;

    const root = getProgressiveConfigRoot();
    if (!root || root.dataset.ccv1ProgressivePricingVerified !== 'true' || !isUsdCart(cart) || hasFixedBundle(cart)) return cart;

    const summary = summarizeCart(cart);
    const pairQuantity = summary.eligiblePairQuantity;
    if (pairQuantity > fallbackProgressiveLadder.length) return cart;

    const ladder = parseProgressiveLadder(root.dataset.ccv1ProgressiveLadder);
    const expectedTotal = getTierTotal(pairQuantity, ladder);
    const expectedSaving = Math.max(0, (pairQuantity * progressiveBasePairPrice) - expectedTotal);
    const expectedCode = getProgressiveDiscountCode(root, pairQuantity);
    const cartDiscountCodes = getCartDiscountCodes(cart);
    const hasCustomerDiscountCode = cartDiscountCodes
      .some((code) => !progressiveDiscountCodeSet.has(code));
    if (hasCustomerDiscountCode) return cart;
    if (pairQuantity === 0) {
      if (!cartDiscountCodes.some((code) => progressiveDiscountCodeSet.has(code))) return cart;
      return runPricingReconciliation(() => updateCart({ discount: '' }).catch(() => cart));
    }
    const evidence = getProgressiveDiscountEvidence(cart);
    const appliedProgressiveCodes = getAppliedDiscountCodes(cart)
      .filter((code) => progressiveDiscountCodeSet.has(code));
    // A merchant automatic promotion is not a stale tier. Do not replace it
    // merely because its total differs from this storefront's discount ladder.
    if (summary.totalDiscount > 0 && appliedProgressiveCodes.length === 0) return cart;
    const tierAlreadyVerified = summary.eligibleOriginalSubtotal === pairQuantity * progressiveBasePairPrice
      && summary.eligibleOriginalSubtotal - evidence.eligibleDiscount === expectedTotal
      && evidence.eligibleDiscount === expectedSaving
      && evidence.excludedDiscount === 0
      && summary.totalDiscount === expectedSaving
      && (pairQuantity === 1
        ? appliedProgressiveCodes.length === 0 && !cartDiscountCodes.some((code) => progressiveDiscountCodeSet.has(code))
        : appliedProgressiveCodes.length === 1 && appliedProgressiveCodes[0] === expectedCode);
    if (tierAlreadyVerified) return cart;

    return runPricingReconciliation(async () => {
      try {
        const reconciledCart = await updateCart({ discount: pairQuantity >= 2 ? expectedCode : '' });
        if (hasVerifiedProgressiveTier(reconciledCart, root, pairQuantity)) return reconciledCart;
        return await updateCart({ discount: getCartDiscountCodeString(cart) });
      } catch (_error) { return cart; }
    });
  };

  const getCartDiscountCodeString = (cart) => getCartDiscountCodes(cart).join(',');

  const hasVerifiedProgressiveTier = (cart, root, pairQuantity) => {
    const ladder = parseProgressiveLadder(root?.dataset.ccv1ProgressiveLadder);
    const summary = summarizeCart(cart);
    const expectedTotal = getTierTotal(pairQuantity, ladder);
    const expectedSaving = Math.max(0, (pairQuantity * progressiveBasePairPrice) - expectedTotal);
    const expectedCode = getProgressiveDiscountCode(root, pairQuantity);
    const evidence = getProgressiveDiscountEvidence(cart);
    const appliedCodes = getAppliedDiscountCodes(cart)
      .filter((code) => progressiveDiscountCodeSet.has(code));
    const discountCodeMatches = pairQuantity === 1
      ? appliedCodes.length === 0
      : appliedCodes.length === 1 && appliedCodes[0] === expectedCode;
    return isUsdCart(cart)
      && !hasFixedBundle(cart)
      && pairQuantity >= 1
      && summary.eligiblePairQuantity === pairQuantity
      && summary.eligibleOriginalSubtotal === pairQuantity * progressiveBasePairPrice
      && summary.eligibleOriginalSubtotal - evidence.eligibleDiscount === expectedTotal
      && evidence.eligibleDiscount === expectedSaving
      && evidence.excludedDiscount === 0
      && summary.totalDiscount === expectedSaving
      && discountCodeMatches;
  };

  const restoreCartAfterFailedDiscount = async (beforeCart, addResult) => {
    const beforeItems = Array.isArray(beforeCart?.items) ? beforeCart.items : [];
    const addedItems = Array.isArray(addResult?.items) ? addResult.items : [];
    const identity = (item) => JSON.stringify([
      Number(item.variant_id || item.id),
      Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
      item.selling_plan_allocation?.selling_plan?.id || null,
    ]);
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Please review your cart before trying again.');
    const current = await response.json();
    const addedIdentities = new Set(addedItems.map(identity));
    const updates = {};
    current.items.forEach((item) => {
      if (!addedIdentities.has(identity(item))) return;
      const priorLine = beforeItems.find((prior) => identity(prior) === identity(item));
      updates[item.key] = Number(priorLine?.quantity || 0);
    });
    return updateCart({
      updates,
      discount: getCartDiscountCodeString(beforeCart),
    });
  };

  let cartMutationBusy = false;
  const beginCartMutation = (invalidateRefresh = true) => {
    if (cartMutationBusy || progressiveReconcilePromise) return false;
    cartMutationBusy = true;
    if (invalidateRefresh) ++cartRefreshSequence;
    document.documentElement.classList.add('ccv1-cart-busy');
    window.dispatchEvent(new CustomEvent('ccv1:cart-busy', { detail: { busy: true } }));
    return true;
  };
  const endCartMutation = () => {
    cartMutationBusy = false;
    document.documentElement.classList.remove('ccv1-cart-busy');
    window.dispatchEvent(new CustomEvent('ccv1:cart-busy', { detail: { busy: false } }));
  };

  const refreshCartSections = async (cart) => {
    const targets = ['main-cart-items', 'main-cart-footer'].map((id) => document.getElementById(id)).filter(Boolean);
    if (targets.length !== 2) return;
    const url = new URL(window.routes?.cart_url || '/cart', window.location.origin);
    // Reading the cart page preserves draft-theme context. Shopify can return
    // null for dynamic section IDs when a development theme is being previewed.
    const response = await fetch(url, { cache: 'no-store', headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Your cart updated. Refresh to see the latest total.');
    const source = new DOMParser().parseFromString(await response.text(), 'text/html');
    targets.forEach((target) => {
      const content = source.querySelector(`#${target.id} .js-contents`);
      const destination = target.querySelector('.js-contents');
      if (!content || !destination) throw new Error('Your cart updated. Refresh to see the latest total.');
      destination.innerHTML = content.innerHTML;
    });
    document.querySelector('cart-items')?.classList.toggle('is-empty', cart.item_count === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
    document.querySelectorAll('[name="checkout"]').forEach((button) => { button.disabled = cart.item_count === 0; });
  };

  const settleCart = async (cart) => {
    cart = await reconcileProgressiveCartPricing(cart);
    await refreshCartSections(cart);
    lastKnownCart = cart;
    syncCartUi(cart);
    renderPdpCartState(cart);
    const liveTotal = document.getElementById('cart-live-region-text');
    if (liveTotal) liveTotal.textContent = `New estimated total: ${formatMoney(cart.total_price)}`;
    publishCartState(cart);
    return cart;
  };

  const getCartOfferPricing = (cart) => {
    const root = getProgressiveConfigRoot();
    const count = summarizeCart(cart).pairQuantity;
    if (!root || root.dataset.ccv1ProgressivePricingVerified !== 'true'
      || count < 1 || count >= fallbackProgressiveLadder.length
      || !hasVerifiedProgressiveTier(cart, root, count)
      || getCartDiscountCodes(cart).some((code) => !progressiveDiscountCodeSet.has(code))) return { nextEligible: false };
    const ladder = parseProgressiveLadder(root.dataset.ccv1ProgressiveLadder);
    const increment = getTierTotal(count + 1, ladder) - getTierTotal(count, ladder);
    return { nextEligible: true, increment, saving: progressiveBasePairPrice - increment,
      targetPairs: count + 1, discountCode: getProgressiveDiscountCode(root, count + 1) };
  };

  window.Ccv1Cart = Object.freeze({
    beginMutation: beginCartMutation, endMutation: endCartMutation, settle: settleCart,
    refresh: refreshCartCount, snapshot: () => lastKnownCart, pricing: getCartOfferPricing, formatMoney,
  });

  const bindAddButtons = () => {
    document.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-ccv1-bundle]');
      if (!button) return;
      event.preventDefault();
      if (cartMutationBusy || button.disabled || button.getAttribute('aria-busy') === 'true') return;

      const parsedItems = parseBundle(button.dataset.ccv1Bundle);
      if (!parsedItems.length) {
        announce('This item is not ready to add yet.', true);
        return;
      }

      const label = button.querySelector('[data-ccv1-button-label]');
      const originalLabel = label?.textContent || button.textContent;
      const title = button.dataset.ccv1BundleTitle || 'Item';
      const productRoot = button.closest('[data-ccv1-flavor-pdp]');
      const offerFamily = button.dataset.ccv1OfferFamily || 'individual';
      const totalBags = Number(button.dataset.ccv1TotalBags) || (parsedItems.length * 2);
      const totalUnits = parsedItems.reduce((total, item) => total + item.quantity, 0);
      const bagsPerLine = Math.max(1, Math.round(totalBags / totalUnits));
      const items = parsedItems.map((item) => ({
        ...item,
        properties: {
          _ccv1_offer_family: offerFamily,
          _ccv1_bag_count: bagsPerLine,
          ...(offerFamily === 'individual' && button.dataset.ccv1ProgressiveEligible === 'true'
            ? { _ccv1_progressive_eligible: 'true' }
            : {}),
        },
      }));
      const controls = Array.from(productRoot?.querySelectorAll(
        '[data-ccv1-bundle], [data-ccv1-flavor-choice], [data-ccv1-pack-option], [data-ccv1-format-tab]'
      ) || [button]);

      if (!beginCartMutation()) return;
      button.setAttribute('aria-busy', 'true');
      controls.forEach((control) => { control.disabled = true; });
      if (label) label.textContent = 'Adding…';

      const advertisedOfferPrice = Number(button.dataset.ccv1CartOfferPrice || 0);
      const advertisedOfferCode = String(button.dataset.ccv1DiscountCode || '');
      const beforeCart = await refreshCartCount({ duringMutation: true });
      const beforeSummary = summarizeCart(beforeCart);
      const hasCustomerDiscountCode = getCartDiscountCodes(beforeCart)
        .some((code) => !progressiveDiscountCodeSet.has(code));
      const requestedDiscountCode = String(button.dataset.ccv1DiscountCode || '').trim();
      const discountCode = hasCustomerDiscountCode
        ? ''
        : requestedDiscountCode;
      const tierTargetQuantity = hasCustomerDiscountCode
        ? 0
        : Number(button.dataset.ccv1TierTargetQuantity || 0);
      const discountedAddTotal = Number(button.dataset.ccv1DiscountedTotal || 0);
      let addUnavailable = false;
      let addConfirmed = false;

      try {
        if (!beforeCart) throw new Error('We could not check your cart. Please try again.');
        if (button.hasAttribute('data-ccv1-cart-offer-add')) {
          const livePricing = getCartOfferPricing(beforeCart);
          const expectedPrice = advertisedOfferCode ? livePricing.increment : Number(button.dataset.ccv1CartOfferBasePrice);
          if (advertisedOfferPrice !== expectedPrice || (advertisedOfferCode && advertisedOfferCode !== livePricing.discountCode)) {
            throw new Error('Your cart changed. Review the updated offer and try again.');
          }
        }
        if (requestedDiscountCode && hasCustomerDiscountCode) {
          throw new Error('Remove your current discount code before adding this offer.');
        }

        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        const result = await response.json();
        if (!response.ok) {
          const failureMessage = String(result.description || result.message || '');
          addUnavailable = response.status === 422 && /sold out|not enough|unavailable|inventory/i.test(failureMessage);
          throw new Error(result.description || result.message || 'Add to cart failed');
        }
        addConfirmed = true;

        if (label) label.textContent = discountCode ? 'Applying your price…' : 'Added to cart';
        let cart;
        if (discountCode) {
          try {
            cart = await updateCart({ discount: discountCode });
            const progressiveTierVerified = offerFamily === 'individual'
              && tierTargetQuantity >= 2
              && hasVerifiedProgressiveTier(cart, productRoot || getProgressiveConfigRoot(), tierTargetQuantity)
              && (!advertisedOfferPrice || Number(cart.total_price) === beforeSummary.finalTotal + advertisedOfferPrice);
            const fixedOfferVerified = offerFamily !== 'individual'
              && discountedAddTotal > 0
              && getAppliedDiscountCodes(cart).includes(discountCode)
              && Number(cart.total_price || 0) === beforeSummary.finalTotal + discountedAddTotal;
            if (!progressiveTierVerified && !fixedOfferVerified) {
              throw new Error('Shopify did not confirm the advertised price.');
            }
          } catch (_discountError) {
            await restoreCartAfterFailedDiscount(beforeCart, result);
            addConfirmed = false;
            throw new Error('We could not apply that lower price, so we removed the add. Please try again.');
          }
        } else {
          cart = await refreshCartCount({ duringMutation: true });
          if (!cart) cart = await refreshCartCount({ duringMutation: true });
        }
        if (!cart) throw new Error('Your item was added. Refresh your cart to see the latest total.');
        if (cart && document.body.classList.contains('template-cart')) cart = await settleCart(cart);
        const afterSummary = summarizeCart(cart || {});

        if (productRoot) {
          productRoot.querySelectorAll('[data-ccv1-add-status]').forEach((status) => {
            status.textContent = cart
              ? `${title} added. Your cart is ${formatMoney(afterSummary.finalTotal)}.`
              : `${title} added to your cart.`;
          });
          if (label && cart) label.textContent = 'Added to cart';
        } else if (button.hasAttribute('data-ccv1-cart-offer-add')) {
          window.dispatchEvent(new CustomEvent('ccv1:cart-offer-added', { detail: { title, cart } }));
          announce(`${title} added.`);
        } else {
          const notificationOpened = openCartNotification(result, cart, button);
          if (!notificationOpened) announce(`${title} was added to your cart.`);
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'cc_add_to_cart',
          item_name: title,
          component_count: items.length,
          variant_ids: items.map((item) => item.id),
          offer_family: offerFamily,
          offer_model: button.dataset.ccv1OfferSource || (items.length > 1 ? 'component_bundle' : 'single_product'),
          pair_quantity_before: beforeSummary.pairQuantity,
          pair_quantity_after: afterSummary.pairQuantity,
          cart_original_total: afterSummary.originalTotal,
          cart_total: afterSummary.finalTotal,
          cart_discount: afterSummary.totalDiscount,
        });
      } catch (error) {
        if (addConfirmed) {
          // A successful add must not become a retry button if rendering fails:
          // that would invite the shopper to add the same merchandise twice.
          announce('Your item was added. Opening your updated cart.');
          window.location.assign(window.routes?.cart_url || '/cart');
          return;
        }
        if (addUnavailable) {
          button.dataset.ccv1Available = 'false';
          button.dataset.ccv1IdleLabel = 'SOLD OUT';
          button.disabled = true;
        }
        if (label) label.textContent = addUnavailable ? 'SOLD OUT' : 'Try again';
        announce(error.message || 'We could not add this item. Try again.', true);
      } finally {
        window.setTimeout(() => {
          endCartMutation();
          button.removeAttribute('aria-busy');
          controls.forEach((control) => {
            control.disabled = control.dataset.available === 'false' || control.dataset.ccv1Available === 'false';
          });
          button.disabled = button.dataset.available === 'false' || button.dataset.ccv1Available === 'false';
          if (label) label.textContent = button.dataset.ccv1IdleLabel || originalLabel.trim();
        }, 950);
      }
    });
  };

  const bindMenu = () => {
    const toggle = document.querySelector('[data-ccv1-menu-toggle]');
    const menu = document.querySelector('[data-ccv1-mobile-menu]');
    if (!toggle || !menu) return;
    const label = toggle.querySelector('.visually-hidden');

    const close = (returnFocus = false) => {
      toggle.setAttribute('aria-expanded', 'false');
      if (label) label.textContent = 'Open menu';
      menu.hidden = true;
      document.body.style.overflow = '';
      if (returnFocus) toggle.focus();
    };

    toggle.addEventListener('click', () => {
      const opening = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(opening));
      if (label) label.textContent = opening ? 'Close menu' : 'Open menu';
      menu.hidden = !opening;
      document.body.style.overflow = opening ? 'hidden' : '';
    });

    menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) close(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !menu.hidden) close(true);
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth >= 990 && !menu.hidden) close(false);
    });
  };

  const bindTasteSequence = () => {
    const root = document.querySelector('[data-ccv1-taste]');
    const dataNode = document.querySelector('[data-ccv1-taste-data]');
    if (!root || !dataNode) return;

    let stages;
    try {
      stages = JSON.parse(dataNode.textContent);
    } catch (_error) {
      return;
    }

    const tabs = Array.from(root.querySelectorAll('[data-ccv1-taste-tab]'));
    const panel = root.querySelector('.ccv1-taste__panel');
    const image = root.querySelector('[data-ccv1-taste-image]');
    const kicker = root.querySelector('[data-ccv1-taste-kicker]');
    const title = root.querySelector('[data-ccv1-taste-title]');
    const copy = root.querySelector('[data-ccv1-taste-copy]');
    const number = root.querySelector('[data-ccv1-taste-number]');
    const progress = root.querySelector('[data-ccv1-taste-progress]');
    if (!tabs.length || !panel || !image || !kicker || !title || !copy || !number || !progress) return;

    const selectStage = (index, focusTab = false) => {
      const stage = stages[index];
      if (!stage) return;

      tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      if (tabs[index]?.id) panel.setAttribute('aria-labelledby', tabs[index].id);

      image.style.opacity = '0';
      image.style.transform = 'scale(0.96)';
      window.setTimeout(() => {
        image.src = stage.image;
        image.alt = stage.alt;
        kicker.textContent = stage.kicker;
        title.textContent = stage.title;
        copy.textContent = stage.copy;
        number.textContent = stage.number;
        panel.dataset.tone = stage.tone;
        progress.style.width = `${((index + 1) / stages.length) * 100}%`;
        progress.style.backgroundColor = index === 2 ? 'var(--ccv1-red)' : index === 1 ? 'var(--ccv1-mango)' : 'var(--ccv1-pink)';
        image.style.opacity = '1';
        image.style.transform = 'scale(1)';
      }, 150);

      if (focusTab) tabs[index].focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectStage(index));
      tab.addEventListener('keydown', (event) => {
        let nextIndex;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (typeof nextIndex === 'number') {
          event.preventDefault();
          selectStage(nextIndex, true);
        }
      });
    });
  };

  const bindRails = () => {
    document.querySelectorAll('[data-ccv1-rail]').forEach((root) => {
      if (root.dataset.ccv1RailBound === 'true') return;
      const track = root.querySelector('[data-ccv1-rail-track]');
      const controls = Array.from(root.querySelectorAll('[data-ccv1-rail-control]'));
      const items = Array.from(root.querySelectorAll('[data-ccv1-rail-item]'));
      const dots = Array.from(root.querySelectorAll('[data-ccv1-rail-dot]'));
      if (!track) return;
      root.dataset.ccv1RailBound = 'true';

      const getStep = () => {
        const firstItem = items[0] || track.firstElementChild;
        const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
        return (firstItem?.getBoundingClientRect().width || track.clientWidth * 0.8) + gap;
      };

      const updateControls = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        controls.forEach((control) => {
          const direction = Number(control.dataset.ccv1RailControl);
          control.disabled = direction < 0 ? track.scrollLeft <= 2 : track.scrollLeft >= maxScroll - 2;
        });

        if (dots.length) {
          const step = getStep();
          const activeIndex = Math.max(0, Math.min(items.length - 1, Math.round(track.scrollLeft / step)));
          dots.forEach((dot, index) => {
            dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
          });
        }
      };

      controls.forEach((control) => {
        control.addEventListener('click', () => {
          const step = getStep();
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          track.scrollBy({
            left: step * Number(control.dataset.ccv1RailControl),
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        });
      });

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.ccv1RailDot);
          if (!Number.isFinite(index)) return;
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          track.scrollTo({
            left: getStep() * index,
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        });
      });

      track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollBy({
          left: getStep() * direction,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      });

      track.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });
      window.requestAnimationFrame(() => {
        const initialIndex = Number(root.dataset.ccv1RailInitialIndex || 0);
        if (Number.isFinite(initialIndex) && initialIndex > 0) {
          track.scrollTo({ left: getStep() * initialIndex, behavior: 'auto' });
        }
        updateControls();
      });
    });
  };

  const bindMediaDialog = () => {
    const dialog = document.querySelector('[data-ccv1-media-dialog]');
    const dialogImage = dialog?.querySelector('[data-ccv1-media-dialog-image]');
    const close = dialog?.querySelector('[data-ccv1-media-close]');
    if (!dialog || !dialogImage || !close) return;

    document.querySelectorAll('[data-ccv1-media-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        dialogImage.src = trigger.dataset.ccv1MediaSrc || '';
        dialogImage.alt = trigger.dataset.ccv1MediaAlt || 'Customer photo';
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
    });

    const closeDialog = () => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      dialogImage.src = '';
    };

    close.addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
  };

  const bindProofVideo = () => {
    document.querySelectorAll('.ccv1-reaction--feature').forEach((root) => {
      const video = root.querySelector('[data-ccv1-proof-video]');
      const play = root.querySelector('[data-ccv1-proof-play]');
      if (!video || !play) return;

      const startPlayback = async () => {
        root.classList.add('is-playing');
        video.controls = true;
        try {
          await video.play();
        } catch (_error) {
          root.classList.remove('is-playing');
          video.controls = false;
        }
      };

      play.addEventListener('click', startPlayback);
      video.addEventListener('ended', () => {
        root.classList.remove('is-playing');
        video.controls = false;
        video.currentTime = 0;
      });
    });
  };

  const bindFlavorProduct = () => {
    document.querySelectorAll('[data-ccv1-flavor-pdp]').forEach((root) => {
      const productGallery = root.querySelector('[data-ccv1-product-gallery]');
      const galleryTrack = productGallery?.querySelector('[data-ccv1-gallery-track]');
      const gallerySlides = Array.from(productGallery?.querySelectorAll('[data-ccv1-gallery-slide]') || []);
      const galleryDots = Array.from(productGallery?.querySelectorAll('[data-ccv1-gallery-dot]') || []);
      const galleryPrevious = productGallery?.querySelector('[data-ccv1-gallery-previous]');
      const galleryNext = productGallery?.querySelector('[data-ccv1-gallery-next]');
      const galleryStatus = productGallery?.querySelector('[data-ccv1-gallery-status]');
      const twoPackImage = productGallery?.querySelector('[data-ccv1-gallery-image="two-pack"]');
      const alternateImage = productGallery?.querySelector('[data-ccv1-gallery-image="alternate"]');
      const candyImage = productGallery?.querySelector('[data-ccv1-gallery-image="candy"]');
      const candySlide = candyImage?.closest('[data-ccv1-gallery-slide]');
      const flavorChoices = Array.from(root.querySelectorAll('[data-ccv1-flavor-choice]'));
      const formatTabs = Array.from(root.querySelectorAll('[data-ccv1-format-tab]'));
      const formatPanels = Array.from(root.querySelectorAll('[data-ccv1-format-panel]'));
      const galleryModes = Array.from(root.querySelectorAll('[data-ccv1-gallery-mode]'));
      const packOptions = Array.from(root.querySelectorAll('[data-ccv1-pack-option]'));
      const packGalleryOffers = Array.from(root.querySelectorAll('[data-ccv1-pack-gallery]'));
      const varietyGalleryLabel = root.querySelector('[data-ccv1-variety-gallery-label]');
      const flavorOnlyContent = Array.from(root.querySelectorAll('[data-ccv1-flavor-only]'));
      const varietyButton = root.querySelector('[data-ccv1-variety-add]');
      const varietyPrice = root.querySelector('[data-ccv1-variety-price]');
      const varietyPerBag = root.querySelector('[data-ccv1-variety-per-bag]');
      const heading = root.querySelector('[data-ccv1-pdp-heading]');
      const lede = root.querySelector('[data-ccv1-pdp-lede]');
      const mobileBuy = root.querySelector('[data-ccv1-mobile-buy]');
      const mobileButton = mobileBuy?.querySelector('[data-ccv1-mobile-add]');
      const mobileTitle = mobileBuy?.querySelector('[data-ccv1-mobile-title]');
      const mobileVariant = mobileBuy?.querySelector('[data-ccv1-mobile-variant]');
      const currentPack = root.querySelector('[data-ccv1-current-pack]');
      const flavorDetails = root.querySelector('[data-ccv1-flavor-details]');
      const flavorAttributes = root.querySelector('[data-ccv1-flavor-attributes]');
      const flavorDetailTemplates = Array.from(root.querySelectorAll('[data-ccv1-flavor-details-template]'));
      let activeMode = 'flavor';
      let selectedPack = packOptions.find((option) => option.classList.contains('is-selected')) || packOptions[0];
      let selectedFlavor = flavorChoices.find((choice) => choice.classList.contains('is-selected')) || flavorChoices[0];
      let activeGalleryIndex = 0;
      let galleryPointerStart = null;

      const setGallerySlide = (requestedIndex, { announce = true } = {}) => {
        if (!gallerySlides.length) return;
        activeGalleryIndex = (requestedIndex + gallerySlides.length) % gallerySlides.length;
        gallerySlides.forEach((slide, index) => {
          const isActive = index === activeGalleryIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
        });
        galleryDots.forEach((dot, index) => {
          const isActive = index === activeGalleryIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', String(isActive));
        });
        if (productGallery) productGallery.dataset.ccv1GalleryIndex = String(activeGalleryIndex);
        if (galleryStatus) {
          const activeImage = gallerySlides[activeGalleryIndex]?.querySelector('img');
          const liveMode = galleryStatus.getAttribute('aria-live') || 'polite';
          if (!announce) galleryStatus.setAttribute('aria-live', 'off');
          galleryStatus.textContent = `Image ${activeGalleryIndex + 1} of ${gallerySlides.length}: ${activeImage?.alt || 'Chili Chews product photo'}`;
          if (!announce) {
            window.requestAnimationFrame(() => galleryStatus.setAttribute('aria-live', liveMode));
          }
        }
      };

      const updateGalleryImage = (image, source, alt) => {
        if (!image || !source) return;
        image.removeAttribute('srcset');
        image.src = source;
        image.alt = alt || '';
      };

      const swapMainImage = ({ source, alt, alternateSource, alternateAlt, candySource, candyAlt, candyFit }) => {
        updateGalleryImage(twoPackImage, source, alt);
        updateGalleryImage(alternateImage, alternateSource, alternateAlt);
        updateGalleryImage(candyImage, candySource, candyAlt);
        if (candySlide) candySlide.dataset.ccv1CandyFit = candyFit === 'cover' ? 'cover' : 'contain';
        setGallerySlide(0, { announce: false });
      };

      galleryPrevious?.addEventListener('click', () => setGallerySlide(activeGalleryIndex - 1));
      galleryNext?.addEventListener('click', () => setGallerySlide(activeGalleryIndex + 1));
      galleryDots.forEach((dot) => {
        dot.addEventListener('click', () => setGallerySlide(Number(dot.dataset.ccv1GalleryDot || 0)));
      });
      productGallery?.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        setGallerySlide(activeGalleryIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });
      galleryTrack?.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        galleryPointerStart = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
        try {
          galleryTrack.setPointerCapture(event.pointerId);
        } catch (_error) {
          // Pointer capture is a progressive enhancement for edge-to-edge swipes.
        }
      });
      galleryTrack?.addEventListener('pointerup', (event) => {
        if (!galleryPointerStart || galleryPointerStart.pointerId !== event.pointerId) return;
        const deltaX = event.clientX - galleryPointerStart.x;
        const deltaY = event.clientY - galleryPointerStart.y;
        galleryPointerStart = null;
        try {
          if (galleryTrack.hasPointerCapture(event.pointerId)) galleryTrack.releasePointerCapture(event.pointerId);
        } catch (_error) {
          // Ignore browsers that release pointer capture automatically.
        }
        if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
        setGallerySlide(activeGalleryIndex + (deltaX < 0 ? 1 : -1));
      });
      galleryTrack?.addEventListener('pointercancel', (event) => {
        galleryPointerStart = null;
        try {
          if (galleryTrack.hasPointerCapture(event.pointerId)) galleryTrack.releasePointerCapture(event.pointerId);
        } catch (_error) {
          // Ignore browsers that release pointer capture automatically.
        }
      });

      const selectFlavor = (choice) => {
        if (!choice) return;
        selectedFlavor = choice;
        const variantId = choice.dataset.variantId || '';
        const productHandle = choice.dataset.productHandle || '';
        const productTitle = choice.dataset.productTitle || 'Chili Chews';
        const price = choice.dataset.price || '';
        const packTitle = choice.dataset.packTitle || '2 Pack';
        const bagCount = Math.max(1, Number(choice.dataset.totalBags) || 2);
        const isAvailable = choice.dataset.available === 'true';
        const isProgressiveEligible = choice.dataset.progressiveEligible === 'true';

        flavorChoices.forEach((item) => {
          const selected = item.dataset.variantId === variantId;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-selected', String(selected));
        });

        root.dataset.ccv1SelectedVariant = variantId;
        root.dataset.ccv1SelectedPriceCents = choice.dataset.priceCents || '';
        root.dataset.ccv1SelectedProduct = productTitle;
        root.dataset.ccv1SelectedHandle = productHandle;
        root.dataset.ccv1SelectedBagCount = String(bagCount);
        root.dataset.ccv1SelectedPackTitle = packTitle;
        root.dataset.ccv1SelectedProgressiveEligible = String(isProgressiveEligible);
        if (heading) {
          heading.dataset.flavorHeading = productTitle;
          if (activeMode === 'flavor') heading.textContent = productTitle;
        }
        root.querySelectorAll('[data-ccv1-selected-title]').forEach((item) => { item.textContent = productTitle; });
        root.querySelectorAll('[data-ccv1-selected-price]').forEach((item) => { item.textContent = price; });
        root.querySelectorAll('[data-ccv1-selected-per-bag]').forEach((item) => { item.textContent = choice.dataset.perBag || ''; });

        const nativeVariantInputs = root.querySelectorAll('[data-ccv1-native-variant]');
        nativeVariantInputs.forEach((input) => { input.value = variantId; });
        root.querySelectorAll('[data-ccv1-individual-add]').forEach((button) => {
          button.dataset.ccv1Bundle = `${variantId}:1`;
          button.dataset.ccv1BundleTitle = productTitle;
          button.dataset.ccv1OfferFamily = 'individual';
          button.dataset.ccv1OfferSource = 'single_product';
          button.dataset.ccv1TotalBags = String(bagCount);
          button.dataset.ccv1Available = String(isAvailable);
          button.dataset.ccv1ProgressiveEligible = String(isProgressiveEligible);
          button.dataset.ccv1DiscountCode = '';
          button.dataset.ccv1DiscountedTotal = '';
          button.dataset.ccv1TierTargetQuantity = '';
          button.disabled = !isAvailable;
          const label = button.querySelector('[data-ccv1-button-label]');
          if (!label) return;
          if (!isAvailable) label.textContent = 'Sold out';
          else label.textContent = 'Add to cart';
          button.dataset.ccv1IdleLabel = label.textContent;
        });

        if (currentPack) currentPack.textContent = packTitle;
        root.querySelectorAll('[data-ccv1-pickle-deal]').forEach((deal) => {
          deal.hidden = productHandle !== 'pickle-belts';
        });
        if (flavorAttributes) flavorAttributes.setAttribute('aria-label', `${productTitle} taste profile`);
        if (flavorDetails && productHandle) {
          const detailsTemplate = flavorDetailTemplates.find((template) => template.dataset.ccv1FlavorDetailsTemplate === productHandle);
          if (detailsTemplate) flavorDetails.replaceChildren(detailsTemplate.content.cloneNode(true));
        }
        if (mobileTitle && (!lastKnownCart || Number(lastKnownCart.item_count) === 0)) mobileTitle.textContent = productTitle;
        if (mobileVariant && (!lastKnownCart || Number(lastKnownCart.item_count) === 0)) {
          mobileVariant.textContent = `${bagCount} ${bagCount === 1 ? 'bag' : 'bags'} · ${price}`;
        }
        if (productGallery) productGallery.setAttribute('aria-label', `${productTitle} product gallery`);
        swapMainImage({
          source: choice.dataset.image,
          alt: choice.dataset.imageAlt,
          alternateSource: choice.dataset.imageAlternate,
          alternateAlt: choice.dataset.imageAlternateAlt,
          candySource: choice.dataset.imageCandy,
          candyAlt: choice.dataset.imageCandyAlt,
          candyFit: choice.dataset.imageCandyFit
        });
        if (lastKnownCart) renderPdpCartState(lastKnownCart);
      };

      const updateMobilePack = (option) => {
        if (!option || !mobileButton) return;
        const isAvailable = option.dataset.available === 'true';
        mobileButton.removeAttribute('data-ccv1-product-add');
        mobileButton.dataset.ccv1Bundle = option.dataset.bundle || '';
        mobileButton.dataset.ccv1BundleTitle = option.dataset.title || 'Variety Pack';
        mobileButton.dataset.ccv1Available = String(isAvailable);
        mobileButton.dataset.ccv1OfferFamily = option.dataset.ccv1OfferFamily || 'variety';
        mobileButton.dataset.ccv1TotalBags = option.dataset.ccv1TotalBags || '';
        mobileButton.dataset.ccv1OfferSource = option.dataset.ccv1OfferSource || (parseBundle(option.dataset.bundle).length > 1 ? 'component-bundle' : 'shopify-product');
        mobileButton.dataset.ccv1DiscountCode = option.dataset.ccv1DiscountCode || '';
        mobileButton.dataset.ccv1DiscountedTotal = option.dataset.ccv1DiscountedTotal || '';
        mobileButton.disabled = !isAvailable || mobileButton.getAttribute('aria-busy') === 'true';
        const cartHasItems = Number(lastKnownCart?.item_count || 0) > 0;
        if (!cartHasItems && mobileTitle) mobileTitle.textContent = option.dataset.title || 'Variety Pack';
        if (!cartHasItems && mobileVariant) mobileVariant.textContent = `${option.dataset.bags || ''} · ${option.dataset.price || ''}`;
        const label = mobileButton.querySelector('[data-ccv1-button-label]');
        if (label) {
          label.textContent = isAvailable ? 'Add' : 'Sold out';
          mobileButton.dataset.ccv1IdleLabel = label.textContent;
        }
      };

      const selectPack = (option) => {
        if (!option) return;
        selectedPack = option;
        packOptions.forEach((item) => {
          const selected = item === option;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });

        const isAvailable = option.dataset.available === 'true';
        if (varietyPrice) varietyPrice.textContent = option.dataset.price || '';
        if (varietyPerBag) varietyPerBag.textContent = option.dataset.perBag || '';
        if (varietyButton) {
          varietyButton.dataset.ccv1Bundle = option.dataset.bundle || '';
          varietyButton.dataset.ccv1BundleTitle = option.dataset.title || 'Variety Pack';
          varietyButton.dataset.ccv1Available = String(isAvailable);
          varietyButton.dataset.ccv1OfferFamily = option.dataset.ccv1OfferFamily || 'variety';
          varietyButton.dataset.ccv1TotalBags = option.dataset.ccv1TotalBags || '';
          varietyButton.dataset.ccv1OfferSource = option.dataset.ccv1OfferSource || (parseBundle(option.dataset.bundle).length > 1 ? 'component-bundle' : 'shopify-product');
          varietyButton.dataset.ccv1DiscountCode = option.dataset.ccv1DiscountCode || '';
          varietyButton.dataset.ccv1DiscountedTotal = option.dataset.ccv1DiscountedTotal || '';
          varietyButton.disabled = !isAvailable || varietyButton.getAttribute('aria-busy') === 'true';
          const label = varietyButton.querySelector('[data-ccv1-button-label]');
          if (label) {
            label.textContent = isAvailable ? `Add ${option.dataset.title || 'Variety Pack'} · ${option.dataset.price || ''}` : 'Sold out';
            varietyButton.dataset.ccv1IdleLabel = label.textContent;
          }
        }
        packGalleryOffers.forEach((offer) => {
          offer.hidden = offer.dataset.ccv1PackGallery !== option.dataset.key;
        });
        if (varietyGalleryLabel) varietyGalleryLabel.textContent = (option.dataset.bags || '').toUpperCase();
        if (currentPack) currentPack.textContent = option.dataset.bags || option.dataset.title || '';
        if (activeMode === 'variety') updateMobilePack(option);
        if (lastKnownCart) renderPdpCartState(lastKnownCart);
      };

      const setMode = (mode, focusPanel = false) => {
        if (mode !== 'flavor' && mode !== 'variety') return;
        activeMode = mode;
        root.dataset.ccv1PackMode = mode;

        formatTabs.forEach((tab) => {
          const selected = tab.dataset.ccv1FormatTab === mode;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        formatPanels.forEach((panel) => {
          const selected = panel.dataset.ccv1FormatPanel === mode;
          panel.hidden = !selected;
          if (selected && focusPanel) panel.focus({ preventScroll: true });
        });
        galleryModes.forEach((gallery) => {
          gallery.hidden = gallery.dataset.ccv1GalleryMode !== mode;
        });
        flavorOnlyContent.forEach((item) => {
          item.hidden = mode !== 'flavor';
        });

        if (heading) heading.textContent = mode === 'variety' ? heading.dataset.varietyHeading : heading.dataset.flavorHeading;
        if (lede) lede.textContent = mode === 'variety' ? lede.dataset.varietyLede : lede.dataset.flavorLede;

        if (mode === 'variety') {
          selectPack(selectedPack);
          if (lastKnownCart) renderPdpCartState(lastKnownCart);
          window.dispatchEvent(new Event('ccv1:sticky-refresh'));
          return;
        }

        if (selectedFlavor) selectFlavor(selectedFlavor);
        if (lastKnownCart) renderPdpCartState(lastKnownCart);
        window.dispatchEvent(new Event('ccv1:sticky-refresh'));
      };

      flavorChoices.forEach((choice, index) => {
        choice.addEventListener('click', () => selectFlavor(choice));
        choice.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const siblings = flavorChoices.filter((item) => item.closest('[data-ccv1-flavor-picker]') === choice.closest('[data-ccv1-flavor-picker]'));
          const siblingIndex = siblings.indexOf(choice);
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const next = siblings[(siblingIndex + direction + siblings.length) % siblings.length];
          next?.focus();
          if (next) selectFlavor(next);
        });
      });
      packOptions.forEach((option) => option.addEventListener('click', () => selectPack(option)));
      root.querySelectorAll('[data-ccv1-open-variety]').forEach((button) => {
        button.addEventListener('click', () => {
          setMode('variety');
          if (button.dataset.ccv1ScrollToVariety !== 'true') return;

          const targetTab = root.querySelector('[data-ccv1-format-tab="variety"]');
          window.setTimeout(() => targetTab?.focus(), 250);
        });
      });
      formatTabs.forEach((tab, index) => {
        tab.addEventListener('click', () => setMode(tab.dataset.ccv1FormatTab));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const nextIndex = (index + direction + formatTabs.length) % formatTabs.length;
          const nextTab = formatTabs[nextIndex];
          nextTab?.focus();
          if (nextTab) setMode(nextTab.dataset.ccv1FormatTab, true);
        });
      });

      if (selectedPack) selectPack(selectedPack);
      if (selectedFlavor) selectFlavor(selectedFlavor);
    });
  };

  const bindDealTimers = () => {
    const timers = Array.from(document.querySelectorAll('[data-ccv1-deal-timer]'))
      .map((element) => ({
        element,
        output: element.querySelector('[data-ccv1-deal-timer-output]'),
        status: element.querySelector('[data-ccv1-deal-timer-status]'),
        endAt: Date.parse(element.dataset.endAt || ''),
        announcedMinute: null,
      }))
      .filter((timer) => timer.output && Number.isFinite(timer.endAt));
    if (!timers.length) return;

    let intervalId = null;
    const pad = (value) => String(value).padStart(2, '0');
    const update = () => {
      const now = Date.now();
      let hasActiveTimer = false;

      timers.forEach((timer) => {
        const remaining = Math.max(0, timer.endAt - now);
        if (remaining <= 0) {
          timer.element.hidden = true;
          return;
        }

        hasActiveTimer = true;
        const productRoot = timer.element.closest('[data-ccv1-flavor-pdp]');
        if (productRoot && productRoot.dataset.ccv1SelectedHandle !== 'pickle-belts') {
          timer.element.hidden = true;
          return;
        }

        timer.element.hidden = false;
        const totalSeconds = Math.floor(remaining / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        timer.output.textContent = days > 0
          ? `${days}D ${pad(hours)}H ${pad(minutes)}M`
          : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

        const totalMinutes = Math.ceil(remaining / 60000);
        if (timer.status && timer.announcedMinute !== totalMinutes) {
          const dayLabel = days === 1 ? 'day' : 'days';
          const hourLabel = hours === 1 ? 'hour' : 'hours';
          const minuteLabel = minutes === 1 ? 'minute' : 'minutes';
          timer.status.textContent = days > 0
            ? `${days} ${dayLabel}, ${hours} ${hourLabel}, and ${minutes} ${minuteLabel} remaining`
            : `${hours} ${hourLabel} and ${minutes} ${minuteLabel} remaining`;
          timer.announcedMinute = totalMinutes;
        }
      });

      if (!hasActiveTimer && intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    update();
    if (timers.some((timer) => timer.endAt > Date.now())) {
      intervalId = window.setInterval(update, 1000);
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') update();
    });
    window.addEventListener('pageshow', update);
  };

  const bindStickyBuy = () => {
    const anchors = Array.from(document.querySelectorAll('[data-ccv1-sticky-anchor]'));
    const legacySources = Array.from(document.querySelectorAll('[data-ccv1-sticky-source]'));
    const visibilityTargets = anchors.length ? anchors : legacySources;
    const sticky = document.querySelector('[data-ccv1-mobile-buy]');
    if (!visibilityTargets.length || !sticky) return;

    const footer = document.querySelector('.ccv1-footer');
    const productRoot = sticky.closest('[data-ccv1-flavor-pdp]');
    let primaryActionVisible = true;
    let footerVisible = false;
    let viewportFrame = null;

    const updateVisibility = () => {
      const shouldShow = window.innerWidth < 990 && !primaryActionVisible && !footerVisible;
      sticky.hidden = !shouldShow;
      sticky.dataset.ccv1Visible = shouldShow ? 'true' : 'false';
    };

    const isRendered = (item) => item.offsetParent !== null && (item.offsetWidth > 0 || item.offsetHeight > 0);

    const calculateVisibility = () => {
      const activeMode = productRoot?.dataset.ccv1PackMode || 'flavor';
      const matchingAnchors = anchors.length
        ? anchors.filter((item) => item.dataset.ccv1StickyAnchor === activeMode && isRendered(item))
        : [];
      const activeAnchors = matchingAnchors.length
        ? matchingAnchors
        : (anchors.length ? anchors.filter(isRendered) : legacySources.filter(isRendered));
      if (!activeAnchors.length) {
        primaryActionVisible = true;
        footerVisible = false;
        updateVisibility();
        return;
      }
      primaryActionVisible = activeAnchors.some((anchor) => {
        const anchorRect = anchor.getBoundingClientRect();
        return anchorRect.bottom > 0 && anchorRect.top < window.innerHeight;
      });
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        footerVisible = footerRect.top < window.innerHeight && footerRect.bottom > 0;
      }
      updateVisibility();
    };

    const scheduleVisibilityCheck = () => {
      if (viewportFrame !== null) return;
      viewportFrame = window.requestAnimationFrame(() => {
        viewportFrame = null;
        calculateVisibility();
      });
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        () => calculateVisibility(),
        { threshold: 0.01 }
      );
      visibilityTargets.forEach((anchor) => observer.observe(anchor));
      if (footer) observer.observe(footer);
    }

    window.addEventListener('scroll', scheduleVisibilityCheck, { passive: true });
    window.addEventListener('resize', scheduleVisibilityCheck, { passive: true });
    window.addEventListener('ccv1:sticky-refresh', scheduleVisibilityCheck);
    calculateVisibility();
  };

  const bindCartUpdates = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (event?.ccv1Settled) return;
      let cart = event?.cartData;
      if (Array.isArray(cart?.items)) {
        try { await settleCart(cart); } catch (error) { announce(error.message, true); }
        return;
      }
      refreshCartCount();
    });
  };

  const bindCartStateRefresh = () => {
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refreshCartCount();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refreshCartCount();
    });
  };

  ready(() => {
    document.addEventListener('submit', (event) => {
      if (cartMutationBusy && (event.target.id === 'cart' || event.submitter?.name === 'checkout')) {
        event.preventDefault();
        announce('Updating your cart…');
      }
    }, true);
    document.addEventListener('click', (event) => {
      if (cartMutationBusy && event.target.closest('[name="checkout"], a[href="/checkout"], .cart__dynamic-checkout-buttons')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    bindAddButtons();
    bindMenu();
    bindTasteSequence();
    bindRails();
    bindMediaDialog();
    bindProofVideo();
    bindFlavorProduct();
    bindDealTimers();
    bindStickyBuy();
    bindCartUpdates();
    bindCartStateRefresh();
    refreshCartCount();
  });
})();
